import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Users, ShoppingBag, TrendingUp, ExternalLink, CheckCircle, X, Bell, RefreshCw, Globe, Trash2, Check, Layers, AlertCircle, ArrowUpRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import AdminProducts from '../components/AdminProducts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (p) => `R${Number(p || 0).toLocaleString('en-ZA', { minimumFractionDigits: 0 })}`;
const devErr = process.env.NODE_ENV !== 'production' ? console.error.bind(console) : () => {};

const STATUS_COLORS = {
  requested: 'bg-[#D4AF37]/20 text-[#9A7E24]',
  confirmed: 'bg-[#2E7D32]/10 text-[#2E7D32]',
  payment_sent: 'bg-blue-50 text-blue-700',
  paid: 'bg-green-50 text-green-700',
  fulfilled: 'bg-[#0A0A0A]/5 text-[#0A0A0A]',
};

const STATUS_STEPS = ['requested', 'confirmed', 'payment_sent', 'paid', 'fulfilled'];
// Pre-computed: status steps excluding payment_sent (used in status-update button group)
const UPDATABLE_STATUS_STEPS = STATUS_STEPS.filter(s => s !== 'payment_sent');
// Pre-computed: size request transition statuses
const SREQ_TRANSITIONS = ['sourcing', 'available', 'unavailable'];
// Precomputed per-status transition lists — avoids .filter() inside render
const SREQ_NEXT_MAP = Object.fromEntries(
  SREQ_TRANSITIONS.map(s => [s, SREQ_TRANSITIONS.filter(t => t !== s)])
);
const STATUS_LABELS = {
  requested: 'New Request',
  confirmed: 'Confirmed',
  payment_sent: 'Payment Sent',
  paid: 'Paid',
  fulfilled: 'Fulfilled',
};

const SREQ_COLORS = {
  pending: 'bg-[#D4AF37]/20 text-[#9A7E24]',
  sourcing: 'bg-blue-50 text-blue-700',
  available: 'bg-[#2E7D32]/10 text-[#2E7D32]',
  unavailable: 'bg-red-50 text-red-700',
};

// ---- Sourcing Dashboard Component ----
const SOURCING_STATUS_COLORS = {
  requested:  { bg: 'bg-[#D4AF37]/15 text-[#9A7E24]', label: 'New Request' },
  confirmed:  { bg: 'bg-[#2E7D32]/10 text-[#2E7D32]', label: 'Confirmed' },
  sourcing:   { bg: 'bg-blue-50 text-blue-700', label: 'Sourcing' },
};

function SourcingDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  // API and axios are module-level constants — stable, intentionally omitted from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchSourcing = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sourcing`, { withCredentials: true });
      setItems(data.items || []);
    } catch { /* silently handle fetch error */
      devErr('[SourcingDashboard] fetchSourcing failed');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSourcing(); }, [fetchSourcing]);

  const updateStatus = async (bagItemId, newStatus) => {
    setUpdatingId(bagItemId);
    try {
      await axios.put(`${API}/admin/orders/${bagItemId}/status`, { status: newStatus }, { withCredentials: true });
      setItems(prev => prev.map(i => i.bag_item_id === bagItemId ? { ...i, status: newStatus } : i));
    } catch { /* silently handle update error */
      devErr('[SourcingDashboard] updateStatus failed');
    }
    finally { setUpdatingId(null); }
  };

  const filtered = useMemo(() =>
    filterStatus === 'all' ? items : items.filter(i => i.status === filterStatus),
    [items, filterStatus]
  );

  // KPI calculations
  const totalValue = useMemo(() => items.reduce((s, i) => s + (i.product?.final_price || 0), 0), [items]);
  const byStatus = useMemo(() => ({
    requested: items.filter(i => i.status === 'requested').length,
    confirmed:  items.filter(i => i.status === 'confirmed').length,
    sourcing:   items.filter(i => i.status === 'sourcing').length,
  }), [items]);

  return (
    <div className="space-y-6" data-testid="sourcing-dashboard">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Items', value: items.length, icon: Layers, color: 'text-[#D4AF37]' },
          { label: 'New Requests', value: byStatus.requested, icon: AlertCircle, color: 'text-orange-500' },
          { label: 'Confirmed', value: byStatus.confirmed, icon: CheckCircle, color: 'text-[#2E7D32]' },
          { label: 'Est. Revenue', value: fmt(totalValue), icon: TrendingUp, color: 'text-[#D4AF37]' },
        ].map(kpi => (
          <div key={kpi.label} className="bg-white border border-[#E5E5E5] p-4 flex items-center gap-3" data-testid={`kpi-${kpi.label.toLowerCase().replace(/ /g,'-')}`}>
            <div className={`${kpi.color}`}><kpi.icon size={18} /></div>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#999999] font-work">{kpi.label}</p>
              <p className="text-lg font-semibold text-[#0A0A0A] font-work">{kpi.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter + Refresh */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {[['all','All'], ['requested','New Requests'], ['confirmed','Confirmed'], ['sourcing','In Sourcing']].map(([v, l]) => (
            <button key={v} onClick={() => setFilterStatus(v)}
              className={`px-4 py-1.5 text-xs uppercase tracking-wider font-work border transition-colors ${filterStatus === v ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'border-[#E5E5E5] text-[#555555] hover:border-[#D4AF37]'}`}
              data-testid={`filter-${v}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={fetchSourcing} className="text-[#999999] hover:text-[#D4AF37] transition-colors" data-testid="refresh-sourcing-btn">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Sourcing Table */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#E5E5E5] bg-[#F9F9F9]">
              {['Product', 'Customer', 'Source', 'Pricing', 'Status', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center"><div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-[#999999] font-work">No items in this stage</td></tr>
            ) : filtered.map(item => {
              const p = item.product;
              const st = SOURCING_STATUS_COLORS[item.status] || SOURCING_STATUS_COLORS.requested;
              const disc = p?.discounted_price || 0;
              const final = p?.final_price || 0;
              const markup = item.markup_pct;
              return (
                <tr key={item.bag_item_id} className="border-b border-[#F3F3F3] hover:bg-[#FAFAFA] transition-colors" data-testid={`sourcing-row-${item.bag_item_id}`}>
                  {/* Product */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-12 bg-[#F3F3F3] overflow-hidden flex-shrink-0">
                        <img src={p?.images?.[0]} alt="" className="object-cover w-full h-full" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-work font-semibold">{p?.designer}</p>
                        <p className="text-xs text-[#0A0A0A] font-work max-w-[140px] truncate">{p?.name}</p>
                        {item.size && <p className="text-[10px] text-[#999999] font-work">Size: {item.size}</p>}
                      </div>
                    </div>
                  </td>
                  {/* Customer */}
                  <td className="px-4 py-4">
                    <p className="text-xs font-medium text-[#0A0A0A] font-work">{item.customer?.name || '—'}</p>
                    <p className="text-[10px] text-[#999999] font-work">{item.customer?.email}</p>
                    {item.created_at && (
                      <p className="text-[10px] text-[#CCCCCC] font-work mt-0.5">
                        {new Date(item.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                      </p>
                    )}
                  </td>
                  {/* Source */}
                  <td className="px-4 py-4">
                    {p?.source_url ? (
                      <a href={p.source_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors px-3 py-1.5 text-[10px] uppercase tracking-wider font-work font-semibold"
                        data-testid={`open-source-${item.bag_item_id}`}>
                        {p.source_website || 'Source'} <ArrowUpRight size={10} />
                      </a>
                    ) : <span className="text-[#CCCCCC] text-xs font-work">—</span>}
                  </td>
                  {/* Pricing */}
                  <td className="px-4 py-4">
                    <p className="text-xs font-semibold text-[#0A0A0A] font-work">{fmt(final)}</p>
                    <p className="text-[10px] text-[#999999] font-work line-through">{fmt(disc)} cost</p>
                    <p className="text-[10px] font-work" style={{color: markup >= 30 ? '#2E7D32' : '#D4AF37'}}>+{markup}% markup</p>
                  </td>
                  {/* Status */}
                  <td className="px-4 py-4">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold font-work px-2 py-1 ${st.bg}`}>{st.label}</span>
                  </td>
                  {/* Actions */}
                  <td className="px-4 py-4">
                    <div className="flex flex-col gap-1.5">
                      {item.status === 'requested' && (
                        <button onClick={() => updateStatus(item.bag_item_id, 'confirmed')}
                          disabled={updatingId === item.bag_item_id}
                          className="text-[10px] uppercase tracking-wider font-work px-3 py-1.5 bg-[#2E7D32]/10 text-[#2E7D32] hover:bg-[#2E7D32] hover:text-white transition-colors disabled:opacity-40"
                          data-testid={`confirm-btn-${item.bag_item_id}`}>
                          Confirm
                        </button>
                      )}
                      {item.status === 'confirmed' && (
                        <button onClick={() => updateStatus(item.bag_item_id, 'sourcing')}
                          disabled={updatingId === item.bag_item_id}
                          className="text-[10px] uppercase tracking-wider font-work px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white transition-colors disabled:opacity-40"
                          data-testid={`sourcing-btn-${item.bag_item_id}`}>
                          Mark Sourcing
                        </button>
                      )}
                      {item.status === 'sourcing' && (
                        <button onClick={() => updateStatus(item.bag_item_id, 'payment_sent')}
                          disabled={updatingId === item.bag_item_id}
                          className="text-[10px] uppercase tracking-wider font-work px-3 py-1.5 bg-[#D4AF37]/15 text-[#9A7E24] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors disabled:opacity-40"
                          data-testid={`payment-btn-${item.bag_item_id}`}>
                          Send Payment
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---- Scraper Panel Component ----
function ScraperPanel() {
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapeMsg, setScrapeMsg] = useState('');
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [batchRunning, setBatchRunning] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const fetchQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/scraper/queue?status=pending`, { withCredentials: true });
      setQueue(data);
    } catch { /* silently handle queue fetch error */
      devErr('[ScraperPanel] fetchQueue failed');
    }
    finally { setQueueLoading(false); }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleScrapeUrl = async (e) => {
    e.preventDefault();
    if (!scrapeUrl.trim()) return;
    setScraping(true);
    setScrapeMsg('');
    try {
      const { data } = await axios.post(`${API}/admin/scraper/scrape-url`, { url: scrapeUrl }, { withCredentials: true });
      setScrapeMsg(`Scraped: "${data.item.name}" — now in review queue`);
      setScrapeUrl('');
      fetchQueue();
    } catch (e) {
      setScrapeMsg(e.response?.data?.detail || 'Could not scrape that URL. Try a direct product page.');
    } finally {
      setScraping(false);
    }
  };

  const handleApprove = async (scrapeId) => {
    try {
      await axios.post(`${API}/admin/scraper/queue/${scrapeId}/approve`, {}, { withCredentials: true });
      setQueue(q => q.filter(i => i.scrape_id !== scrapeId));
    } catch (e) { alert(e.response?.data?.detail || 'Approve failed'); }
  };

  const handleReject = async (scrapeId) => {
    try {
      await axios.delete(`${API}/admin/scraper/queue/${scrapeId}`, { withCredentials: true });
      setQueue(q => q.filter(i => i.scrape_id !== scrapeId));
    } catch (e) { alert('Reject failed'); }
  };

  const handleBatchScrape = async (scraperId) => {
    setBatchRunning(true);
    setScrapeMsg('');
    try {
      const { data } = await axios.post(
        `${API}/admin/scraper/run/${scraperId}`,
        { queries: ['off-white', 'balenciaga', 'gucci', 'prada', 'valentino'] },
        { withCredentials: true }
      );
      setScrapeMsg(`Batch complete — ${data.queued} new products queued for review`);
      fetchQueue();
    } catch (e) {
      setScrapeMsg(e.response?.data?.detail || 'Batch scrape failed');
    } finally {
      setBatchRunning(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.scrape_id);
    setEditData({
      name: item.name,
      designer: item.designer,
      category: item.category,
      original_price: item.original_price,
      discounted_price: item.discounted_price,
    });
  };

  const saveEdit = async (scrapeId) => {
    try {
      const { data } = await axios.put(`${API}/admin/scraper/queue/${scrapeId}`, editData, { withCredentials: true });
      setQueue(q => q.map(i => i.scrape_id === scrapeId ? data : i));
      setEditingId(null);
    } catch (e) { alert('Save failed'); }
  };

  return (
    <div className="space-y-8" data-testid="scraper-panel">
      {/* URL Scraper */}
      <div className="bg-white border border-[#E5E5E5] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Globe size={16} className="text-[#D4AF37]" />
          <h3 className="text-sm font-semibold text-[#0A0A0A] font-work uppercase tracking-wider">Scrape Product URL</h3>
        </div>
        <p className="text-xs text-[#999999] font-work mb-4">Paste a direct product page URL from End Clothing, SSENSE, Mytheresa, or Mr Porter. The scraper will extract product details and add them to your review queue.</p>
        <form onSubmit={handleScrapeUrl} className="flex gap-3 flex-col sm:flex-row">
          <input
            type="url"
            value={scrapeUrl}
            onChange={e => setScrapeUrl(e.target.value)}
            placeholder="https://www.endclothing.com/en/..."
            className="flex-1 border border-[#E5E5E5] px-4 py-2.5 text-sm font-work focus:border-[#D4AF37] outline-none"
            data-testid="scrape-url-input"
          />
          <button
            type="submit"
            disabled={scraping}
            className="bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors px-6 py-2.5 text-xs uppercase tracking-widest font-work font-semibold disabled:opacity-60 flex items-center gap-2 whitespace-nowrap"
            data-testid="scrape-url-btn"
          >
            {scraping ? <><RefreshCw size={13} className="animate-spin" /> Scraping...</> : 'Scrape URL'}
          </button>
        </form>
        {scrapeMsg && (
          <p className={`mt-3 text-sm font-work ${scrapeMsg.startsWith('Scraped') || scrapeMsg.includes('complete') ? 'text-[#2E7D32]' : 'text-[#D32F2F]'}`} data-testid="scrape-msg">
            {scrapeMsg}
          </p>
        )}
      </div>

      {/* Batch Scrapers */}
      <div className="bg-white border border-[#E5E5E5] p-6">
        <div className="flex items-center gap-2 mb-4">
          <RefreshCw size={16} className="text-[#D4AF37]" />
          <h3 className="text-sm font-semibold text-[#0A0A0A] font-work uppercase tracking-wider">Batch Scrape</h3>
        </div>
        <p className="text-xs text-[#999999] font-work mb-4">Run a batch scrape across supported sites. Searches for top luxury brands and queues results for your review. New products are added every 6 hours automatically.</p>
        <div className="flex flex-wrap gap-3">
          {[
            { id: 'end_clothing', name: 'End Clothing', icon: '🇬🇧' },
            { id: 'ssense', name: 'SSENSE', icon: '🌎' },
          ].map(site => (
            <button
              key={site.id}
              onClick={() => handleBatchScrape(site.id)}
              disabled={batchRunning}
              className="border border-[#E5E5E5] hover:border-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 text-xs uppercase tracking-wider font-work transition-colors disabled:opacity-60 flex items-center gap-2"
              data-testid={`batch-btn-${site.id}`}
            >
              {batchRunning ? <RefreshCw size={12} className="animate-spin" /> : null}
              {site.icon} {site.name}
            </button>
          ))}
        </div>
      </div>

      {/* Review Queue */}
      <div className="bg-white border border-[#E5E5E5] p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Package size={16} className="text-[#D4AF37]" />
            <h3 className="text-sm font-semibold text-[#0A0A0A] font-work uppercase tracking-wider">
              Review Queue {queue.length > 0 && <span className="ml-2 bg-[#D4AF37] text-[#0A0A0A] text-[10px] px-2 py-0.5 font-bold">{queue.length}</span>}
            </h3>
          </div>
          <button onClick={fetchQueue} className="text-[#999999] hover:text-[#D4AF37] transition-colors" data-testid="refresh-queue-btn">
            <RefreshCw size={14} />
          </button>
        </div>

        {queueLoading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>
        ) : queue.length === 0 ? (
          <p className="text-sm text-[#999999] font-work text-center py-8">No products pending review. Use the URL scraper above or run a batch scrape.</p>
        ) : (
          <div className="space-y-4">
            {queue.map(item => (
              <div key={item.scrape_id} className="border border-[#E5E5E5] p-4 flex gap-4" data-testid={`queue-item-${item.scrape_id}`}>
                {/* Thumbnail */}
                {item.images?.[0] && (
                  <div className="w-16 h-20 flex-shrink-0 bg-[#F9F9F9] overflow-hidden">
                    <img src={item.images[0]} alt={item.name} className="object-cover w-full h-full" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {editingId === item.scrape_id ? (
                    <div className="space-y-2">
                      <input className="w-full border border-[#E5E5E5] px-2 py-1 text-sm font-work" value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} />
                      <div className="flex gap-2">
                        <input className="flex-1 border border-[#E5E5E5] px-2 py-1 text-xs font-work" value={editData.designer} onChange={e => setEditData({...editData, designer: e.target.value})} placeholder="Designer" />
                        <select className="flex-1 border border-[#E5E5E5] px-2 py-1 text-xs font-work" value={editData.category} onChange={e => setEditData({...editData, category: e.target.value})}>
                          {['Bags','Shoes','Clothing','Accessories'].map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <input type="number" className="flex-1 border border-[#E5E5E5] px-2 py-1 text-xs font-work" value={editData.original_price} onChange={e => setEditData({...editData, original_price: Number(e.target.value)})} placeholder="Original ZAR" />
                        <input type="number" className="flex-1 border border-[#E5E5E5] px-2 py-1 text-xs font-work" value={editData.discounted_price} onChange={e => setEditData({...editData, discounted_price: Number(e.target.value)})} placeholder="Discounted ZAR" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => saveEdit(item.scrape_id)} className="bg-[#D4AF37] text-[#0A0A0A] px-3 py-1 text-xs font-work">Save</button>
                        <button onClick={() => setEditingId(null)} className="border border-[#E5E5E5] px-3 py-1 text-xs font-work text-[#999999]">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-work font-semibold">{item.designer}</p>
                      <p className="text-sm font-work text-[#0A0A0A] font-medium leading-tight mb-1">{item.name}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-[#555555] font-work mb-2">
                        <span>{item.category}</span>
                        <span>·</span>
                        <span>{item.source_website}</span>
                        {item.discounted_price > 0 && <><span>·</span><span>{fmt(item.final_price)} HEIR LUXX</span></>}
                      </div>
                    </>
                  )}
                </div>
                {editingId !== item.scrape_id && (
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleApprove(item.scrape_id)}
                      className="flex items-center gap-1 bg-[#2E7D32]/10 text-[#2E7D32] hover:bg-[#2E7D32] hover:text-white transition-colors px-3 py-1.5 text-[10px] uppercase tracking-wider font-work"
                      data-testid={`approve-btn-${item.scrape_id}`}
                    >
                      <Check size={11} /> Publish
                    </button>
                    <button
                      onClick={() => startEdit(item)}
                      className="border border-[#E5E5E5] text-[#555555] hover:border-[#D4AF37] hover:text-[#D4AF37] px-3 py-1.5 text-[10px] uppercase tracking-wider font-work"
                      data-testid={`edit-btn-${item.scrape_id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleReject(item.scrape_id)}
                      className="border border-[#E5E5E5] text-[#999999] hover:border-red-400 hover:text-red-500 px-3 py-1.5 text-[10px] uppercase tracking-wider font-work"
                      data-testid={`reject-btn-${item.scrape_id}`}
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sourcing');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [sizeRequests, setSizeRequests] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  // API and axios are module-level constants — stable, intentionally omitted from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, sourcingRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/sourcing/stats`, { withCredentials: true }),
      ]);
      setStats({ ...statsRes.data, sourcing: Object.values(sourcingRes.data).reduce((a, b) => a + b, 0) });
    } catch { /* silently swallow admin stat errors */
      devErr('[Admin] fetchStats failed');
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOrders = useCallback(async (status = '') => {
    try {
      const url = status ? `${API}/admin/orders?status=${status}` : `${API}/admin/orders`;
      const { data } = await axios.get(url, { withCredentials: true });
      setOrders(data);
    } catch { /* silently swallow order fetch errors */
      devErr('[Admin] fetchOrders failed');
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchSizeRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/size-requests`, { withCredentials: true });
      setSizeRequests(data);
    } catch { /* silently swallow size request fetch errors */
      devErr('[Admin] fetchSizeRequests failed');
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchCustomers = useCallback(async () => {
    setCustomersLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/customers`, { withCredentials: true });
      setCustomers(data.customers || []);
    } catch { /* silently handle customers fetch error */
      devErr('[Admin] fetchCustomers failed');
    }
    setCustomersLoading(false);
  }, []);

  useEffect(() => {
    if (!authLoading && user?.role !== 'admin') navigate('/auth', { state: { from: '/admin' } });
    if (user?.role === 'admin') {
      fetchStats();
      fetchOrders();
      fetchSizeRequests();
    }
  }, [user, authLoading, navigate, fetchStats, fetchOrders, fetchSizeRequests]);

  useEffect(() => {
    if (activeTab === 'customers' && user?.role === 'admin') fetchCustomers();
  }, [activeTab, user, fetchCustomers]);

  const updateSizeRequestStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/admin/size-requests/${requestId}/status`, { status: newStatus }, { withCredentials: true });
      setSizeRequests(sizeRequests.map(r => r.request_id === requestId ? { ...r, status: newStatus } : r));
      fetchStats();
    } catch { /* silently handle update error */
      devErr('[Admin] updateSizeRequestStatus failed');
    }
  };

  const updateStatus = async (bagItemId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${bagItemId}/status`, { status: newStatus }, { withCredentials: true });
      setOrders(orders.map(o => o.bag_item_id === bagItemId ? { ...o, status: newStatus } : o));
      if (selectedOrder?.bag_item_id === bagItemId) setSelectedOrder({ ...selectedOrder, status: newStatus });
      setStatusMsg(`Status updated to: ${STATUS_LABELS[newStatus]}`);
      setTimeout(() => setStatusMsg(''), 3000);
      fetchStats();
    } catch { /* silently handle status update error */
      devErr('[Admin] updateStatus failed');
    }
  };

  const generatePaymentLink = async (bagItemId) => {
    setPaymentLoading(true);
    setPaymentUrl('');
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${bagItemId}/payment-link`,
        {},
        { withCredentials: true, headers: { origin: window.location.origin } }
      );
      setPaymentUrl(data.payment_url);
      setOrders(orders.map(o => o.bag_item_id === bagItemId ? { ...o, status: 'payment_sent', payment_url: data.payment_url } : o));
      if (selectedOrder?.bag_item_id === bagItemId) setSelectedOrder({ ...selectedOrder, status: 'payment_sent', payment_url: data.payment_url });
      fetchStats();
    } catch {
      setStatusMsg('Failed to generate payment link');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (authLoading || !user) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (user?.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-[#F9F9F9]" data-testid="admin-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5] px-6 lg:px-12 py-5 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold font-work mb-0.5">Personal Shopper</p>
          <h1 className="font-playfair text-2xl text-[#0A0A0A]">Dashboard</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#555555] font-work hidden sm:block">{user.name}</span>
          <button
            onClick={() => navigate('/')}
            className="border border-[#E5E5E5] text-[#0A0A0A] hover:border-[#D4AF37] transition-colors px-4 py-2 text-xs uppercase tracking-wider font-work"
          >
            View Store
          </button>
        </div>
      </div>

      <div className="px-6 lg:px-12 py-8 max-w-7xl mx-auto">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
            {[
              { label: 'Pending', value: stats.pending, icon: <ShoppingBag size={18} />, color: 'text-[#D4AF37]' },
              { label: 'Payment Sent', value: stats.payment_sent, icon: <TrendingUp size={18} />, color: 'text-blue-600' },
              { label: 'Completed', value: stats.paid, icon: <CheckCircle size={18} />, color: 'text-[#2E7D32]' },
              { label: 'Size Requests', value: stats.size_requests ?? 0, icon: <Bell size={18} />, color: 'text-orange-500' },
              { label: 'Products', value: stats.products, icon: <Package size={18} />, color: 'text-[#0A0A0A]' },
              { label: 'Customers', value: stats.customers, icon: <Users size={18} />, color: 'text-[#0A0A0A]' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-[#E5E5E5] p-5">
                <div className={`mb-3 ${s.color}`}>{s.icon}</div>
                <p className="text-2xl font-bold text-[#0A0A0A] font-work">{s.value}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-[#999999] font-work mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-0 mb-6 border-b border-[#E5E5E5] overflow-x-auto">
          {[
            { id: 'sourcing', label: `Sourcing${stats?.sourcing ? ` (${stats.sourcing})` : ''}` },
            { id: 'orders', label: 'Orders' },
            { id: 'size-requests', label: `Size Requests${stats?.size_requests ? ` (${stats.size_requests})` : ''}` },
            { id: 'products', label: `Products (${stats?.products || 0})` },
            { id: 'customers', label: `Customers (${stats?.customers || 0})` },
            { id: 'scraper', label: 'Scraper' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-xs uppercase tracking-widest font-semibold font-work border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-transparent text-[#999999] hover:text-[#0A0A0A]'}`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Status Message */}
        {statusMsg && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 py-3 mb-6 text-sm font-work text-[#0A0A0A]">
            {statusMsg}
          </div>
        )}

        {/* ── SOURCING TAB ── */}
        {activeTab === 'sourcing' && <SourcingDashboard />}

        {/* ── ORDERS TAB ── */}
        {activeTab === 'orders' && (<>
        {/* Filters */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0A0A0A] font-work">Filter:</p>
          {[{ label: 'All', value: '' }, ...STATUS_STEPS.map(s => ({ label: STATUS_LABELS[s], value: s }))].map(f => (
            <button
              key={f.value}
              onClick={() => { setFilterStatus(f.value); fetchOrders(f.value); }}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider font-work transition-colors ${filterStatus === f.value ? 'bg-[#0A0A0A] text-white' : 'border border-[#E5E5E5] text-[#555555] hover:border-[#D4AF37] hover:text-[#D4AF37]'}`}
              data-testid={`status-filter-${f.value || 'all'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Orders Table */}
        <div className="bg-white border border-[#E5E5E5] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E5E5] bg-[#F9F9F9]">
                  <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work">Product</th>
                  <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work hidden sm:table-cell">Customer</th>
                  <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work hidden md:table-cell">Source</th>
                  <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work">Price</th>
                  <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work">Status</th>
                  <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-[#999999] font-work">No orders found</td></tr>
                ) : orders.map(order => {
                  const p = order.product;
                  return (
                    <tr key={order.bag_item_id} className="border-b border-[#F3F3F3] hover:bg-[#F9F9F9] transition-colors" data-testid={`admin-order-${order.bag_item_id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-12 bg-[#F3F3F3] overflow-hidden flex-shrink-0">
                            <img src={p?.images?.[0]} alt="" className="object-cover w-full h-full" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-[#D4AF37] font-work">{p?.designer}</p>
                            <p className="text-sm text-[#0A0A0A] font-work max-w-[160px] truncate">{p?.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 hidden sm:table-cell">
                        <p className="text-sm text-[#0A0A0A] font-work">{order.customer?.name}</p>
                        <p className="text-xs text-[#999999] font-work">{order.customer?.email}</p>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        {p?.source_url ? (
                          <a
                            href={p.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-[#555555] hover:text-[#D4AF37] font-work"
                            data-testid={`source-link-${order.bag_item_id}`}
                          >
                            {p.source_website} <ExternalLink size={10} />
                          </a>
                        ) : <span className="text-xs text-[#CCCCCC] font-work">—</span>}
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm font-semibold text-[#0A0A0A] font-work">{fmt(p?.final_price)}</p>
                        <p className="text-[10px] text-[#D4AF37] font-work">Save {fmt(p?.savings)}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 font-work ${STATUS_COLORS[order.status] || 'bg-[#F3F3F3] text-[#555555]'}`}>
                          {STATUS_LABELS[order.status] || order.status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <button
                          onClick={() => { setSelectedOrder(order); setPaymentUrl(''); }}
                          className="text-xs uppercase tracking-wider font-work text-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
                          data-testid={`manage-btn-${order.bag_item_id}`}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="border-b border-[#E5E5E5] px-6 py-5 flex items-center justify-between">
              <h2 className="font-playfair text-xl text-[#0A0A0A]">Order Details</h2>
              <button onClick={() => setSelectedOrder(null)} className="text-[#AAAAAA] hover:text-[#0A0A0A]"><X size={20} /></button>
            </div>

            <div className="p-6 flex flex-col gap-6">
              {/* Product */}
              <div className="flex gap-4">
                <div className="w-16 h-20 bg-[#F9F9F9] overflow-hidden flex-shrink-0">
                  <img src={selectedOrder.product?.images?.[0]} alt="" className="object-cover w-full h-full" />
                </div>
                <div>
                  <p className="text-xs text-[#D4AF37] font-semibold font-work uppercase">{selectedOrder.product?.designer}</p>
                  <p className="text-sm font-work text-[#0A0A0A] font-medium">{selectedOrder.product?.name}</p>
                  <p className="text-lg font-bold font-work text-[#0A0A0A] mt-1">{fmt(selectedOrder.product?.final_price)}</p>
                </div>
              </div>

              {/* Customer */}
              <div className="bg-[#F9F9F9] p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold font-work mb-3">Customer</p>
                <p className="text-sm font-work text-[#0A0A0A]">{selectedOrder.customer?.name}</p>
                <p className="text-sm font-work text-[#555555]">{selectedOrder.customer?.email}</p>
                {selectedOrder.customer?.phone && <p className="text-sm font-work text-[#555555]">{selectedOrder.customer.phone}</p>}
              </div>

              {/* Source */}
              {selectedOrder.product?.source_url && (
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold font-work mb-2">Source</p>
                  <a
                    href={selectedOrder.product.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm font-work text-[#0A0A0A] hover:text-[#D4AF37] transition-colors"
                  >
                    {selectedOrder.product.source_website} <ExternalLink size={14} />
                  </a>
                </div>
              )}

              {/* Status Update */}
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold font-work mb-3">Update Status</p>
                <div className="flex flex-wrap gap-2">
                  {UPDATABLE_STATUS_STEPS.map(s => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selectedOrder.bag_item_id, s)}
                      disabled={selectedOrder.status === s}
                      className={`px-3 py-2 text-xs uppercase tracking-wider font-work transition-colors ${selectedOrder.status === s ? 'bg-[#0A0A0A] text-white' : 'border border-[#E5E5E5] text-[#555555] hover:border-[#D4AF37] hover:text-[#D4AF37]'} disabled:cursor-default`}
                      data-testid={`status-btn-${s}`}
                    >
                      {STATUS_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Link */}
              {['requested', 'confirmed'].includes(selectedOrder.status) && (
                <div>
                  <button
                    onClick={() => generatePaymentLink(selectedOrder.bag_item_id)}
                    disabled={paymentLoading}
                    className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors py-4 text-xs uppercase tracking-widest font-semibold font-work disabled:opacity-50"
                    data-testid="generate-payment-btn"
                  >
                    {paymentLoading ? 'Generating...' : 'Generate & Send Payment Link'}
                  </button>
                </div>
              )}

              {/* Show Payment URL */}
              {(paymentUrl || selectedOrder.payment_url) && (
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4">
                  <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold font-work mb-2">Payment Link Generated</p>
                  <a
                    href={paymentUrl || selectedOrder.payment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-work text-[#0A0A0A] hover:text-[#D4AF37] flex items-center gap-1 break-all"
                    data-testid="payment-link"
                  >
                    View Payment Link <ExternalLink size={12} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </>)}

        {/* ── SIZE REQUESTS TAB ── */}
        {activeTab === 'size-requests' && (
          <div className="bg-white border border-[#E5E5E5] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E5E5E5] bg-[#F9F9F9]">
                    <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work">Product</th>
                    <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work">Size</th>
                    <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work hidden sm:table-cell">Customer</th>
                    <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work">Status</th>
                    <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sizeRequests.length === 0 ? (
                    <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-[#999999] font-work">No size requests yet</td></tr>
                  ) : sizeRequests.map(req => (
                    <tr key={req.request_id} className="border-b border-[#F3F3F3] hover:bg-[#F9F9F9] transition-colors" data-testid={`size-req-${req.request_id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-12 bg-[#F3F3F3] overflow-hidden flex-shrink-0">
                            <img src={req.product?.images?.[0]} alt="" className="object-cover w-full h-full" />
                          </div>
                          <div>
                            <p className="text-[10px] text-[#D4AF37] font-semibold font-work uppercase">{req.designer}</p>
                            <p className="text-sm font-work text-[#0A0A0A]">{req.product_name}</p>
                            {req.product && <p className="text-xs text-[#999999] font-work">{fmt(req.product.final_price)}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm font-semibold font-work text-[#0A0A0A] bg-[#F3F3F3] px-2 py-1">{req.size}</span>
                      </td>
                      <td className="px-5 py-4 hidden sm:table-cell">
                        <p className="text-sm font-work text-[#0A0A0A]">{req.customer_name}</p>
                        <p className="text-xs text-[#999999] font-work">{req.customer_email}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 font-semibold font-work ${SREQ_COLORS[req.status] || 'bg-[#F3F3F3] text-[#555555]'}`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex gap-2 flex-wrap">
                          {(SREQ_NEXT_MAP[req.status] || SREQ_TRANSITIONS).map(s => (
                            <button
                              key={s}
                              onClick={() => updateSizeRequestStatus(req.request_id, s)}
                              className="px-2 py-1 text-[10px] uppercase tracking-wider font-work border border-[#E5E5E5] text-[#555555] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── PRODUCTS TAB ── */}
        {activeTab === 'products' && <AdminProducts />}

        {/* ── CUSTOMERS TAB ── */}
        {activeTab === 'customers' && (
          <div className="bg-white border border-[#E5E5E5] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#E5E5E5] bg-[#F9F9F9] flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#555555] font-work">
                {customers.length} Registered Customer{customers.length !== 1 ? 's' : ''}
              </p>
              <button onClick={fetchCustomers} className="text-[#AAAAAA] hover:text-[#D4AF37] transition-colors" data-testid="refresh-customers-btn">
                <RefreshCw size={14} className={customersLoading ? 'animate-spin' : ''} />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="customers-table">
                <thead>
                  <tr className="border-b border-[#E5E5E5]">
                    <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work">Name</th>
                    <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work">Email</th>
                    <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work hidden sm:table-cell">Role</th>
                    <th className="px-5 py-4 text-left text-[10px] uppercase tracking-[0.2em] font-semibold text-[#999999] font-work hidden md:table-cell">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {customersLoading ? (
                    <tr><td colSpan={4} className="px-5 py-16 text-center">
                      <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" />
                    </td></tr>
                  ) : customers.length === 0 ? (
                    <tr><td colSpan={4} className="px-5 py-12 text-center text-sm text-[#999999] font-work">No customers yet</td></tr>
                  ) : customers.map(c => (
                    <tr key={c.user_id} className="border-b border-[#F3F3F3] hover:bg-[#FAFAFA] transition-colors" data-testid={`customer-row-${c.user_id}`}>
                      <td className="px-5 py-4">
                        <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] text-xs font-semibold font-work inline-flex mr-3">
                          {(c.name || c.email || '?')[0].toUpperCase()}
                        </div>
                        <span className="text-sm text-[#0A0A0A] font-work">{c.name || '—'}</span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-sm text-[#555555] font-work">{c.email}</p>
                      </td>
                      <td className="px-5 py-4 hidden sm:table-cell">
                        <span className="text-[10px] uppercase tracking-wider px-2 py-1 font-work font-semibold bg-[#F3F3F3] text-[#555555]">
                          {c.role || 'customer'}
                        </span>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <p className="text-xs text-[#999999] font-work">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── SCRAPER TAB ── */}
        {activeTab === 'scraper' && <ScraperPanel />}

      </div>
    </div>
  );
}
